import { isAxiosError } from "axios";

import { ErrorMessages } from "src/constants/api";
import { TryCatchError } from "src/types";

export const handlingAPIError = (error: unknown): { isAuthIssue: boolean; error: string } => {
  if (isAxiosError(error)) {
    const { response, code } = error;
    // Handle known Axios internal error codes (non-HTTP)
    switch (code) {
      case "ERR_NETWORK":
        return {
          isAuthIssue: false,
          error: !navigator.onLine
            ? "You are offline. Please check your internet connection."
            : "Network error. Unable to connect to the server. The server may be down or not responding. Please try again later or contact support.",
        };
      case "ECONNABORTED":
        return {
          isAuthIssue: false,
          error:
            "The request took too long to complete and was aborted. This could be due to a slow network or server delay.",
        };
      case "ETIMEDOUT":
        return {
          isAuthIssue: false,
          error:
            "The request timed out. Your network might be unstable. Please try again.",
        };
      case "ERR_BAD_REQUEST":
        if (response && response.data) {
          const { code } = response.data as { error: string; status: string; code: number; };
          if (Number(code) === 401) {
            return {
              isAuthIssue: true,
              error: "Your session has been expired"
            }
          } else {
            return {
              isAuthIssue: false,
              error: "The request sent to the server was invalid. Please check the request parameters and try again.",
            }
          }
          // if (status === "unprocessable_entity" && error.includes("firstname_lastname_dob"))
          //   return {
          //     isAuthIssue: false,
          //     error: "Your are trying to create a patient that already exists in our system."
          //   }
        }
        return {
          isAuthIssue: false,
          error:
            "The request sent to the server was invalid. Please check the request parameters and try again.",
        };
      case "ERR_BAD_RESPONSE":
        return {
          isAuthIssue: false,
          error:
            "The server sent an unexpected or malformed response. Please try again later.",
        };
      case "ERR_FR_TOO_MANY_REDIRECTS":
        return {
          isAuthIssue: false,
          error:
            "Too many redirects occurred. The server may be misconfigured or the request URL may be incorrect.",
        };
      case "ERR_CANCELED":
        return {
          isAuthIssue: false,
          error: "The request was canceled by the client or application.",
        };
      case "ERR_INVALID_URL":
        return {
          isAuthIssue: false,
          error:
            "The URL provided for the request is invalid. Please contact support or check the configuration.",
        };
      case "ERR_BAD_OPTION":
        return {
          isAuthIssue: false,
          error:
            "Invalid configuration option was passed to Axios. Please contact support or review the request setup.",
        };
      case "ERR_BAD_OPTION_VALUE":
        return {
          isAuthIssue: false,
          error:
            "An invalid value was set for one of the Axios configuration options. This could be a code issue.",
        };
      case "ERR_NOT_SUPPORT":
        return {
          isAuthIssue: false,
          error:
            "This feature is not supported in the current Axios or browser environment. Please check compatibility.",
        };
      case "ERR_DEPRECATED":
        return {
          isAuthIssue: false,
          error:
            "A deprecated feature or method was used. Consider updating the request or contact support.",
        };
    }

    // Fallback to response-based error handling
    if (response) {
      const status = response.status;
      const data = response.data as any;
      const errorMessage = data?.error || ErrorMessages.NetworkError;

      switch (status) {
        case 400:
          return {
            isAuthIssue: false,
            error:
              errorMessage ||
              "Bad Request: The server could not understand your request due to invalid syntax or parameters.",
          };
        case 401:
          return {
            isAuthIssue: true,
            error:
              "Unauthorized: Your session has expired or you are not logged in. Please log in again.",
          };
        case 403:
          return {
            isAuthIssue: false,
            error:
              "Forbidden: You do not have permission to perform this action. Please check your access rights.",
          };
        case 404:
          return {
            isAuthIssue: false,
            error:
              "Not Found: The requested resource could not be found. It may have been removed or the URL is incorrect.",
          };
        case 409:
          return {
            isAuthIssue: false,
            error:
              "Conflict: The request could not be completed due to a conflict with the current state of the resource.",
          };
        case 422:
          return {
            isAuthIssue: false,
            error:
              "Unprocessable Entity: The request was well-formed but was unable to be followed due to semantic errors.",
          };
        case 429:
          return {
            isAuthIssue: false,
            error:
              "Too Many Requests: You have exceeded the allowed number of requests. Please wait and try again later.",
          };
        case 500:
          return {
            isAuthIssue: false,
            error:
              "Internal Server Error: Something went wrong on our end. Please try again later or contact support.",
          };
        case 502:
          return {
            isAuthIssue: false,
            error:
              "Bad Gateway: The server received an invalid response from the upstream server. Please try again later.",
          };
        case 503:
          return {
            isAuthIssue: false,
            error:
              "Service Unavailable: The server is currently unavailable. Please try again later.",
          };
        case 504:
          return {
            isAuthIssue: false,
            error:
              "Gateway Timeout: The server didn’t respond in time. Please check your connection or try again later.",
          };
        default:
          return {
            isAuthIssue: false,
            error:
              errorMessage ||
              `An unexpected error occurred (status: ${status}). Please try again or contact support.`,
          };
      }
    }

    // No response (e.g., server down)
    return {
      isAuthIssue: false,
      error:
        ErrorMessages.NetworkError ||
        "The server did not respond. It may be down or unreachable. Please try again later.",
    };
  }

  // Non-Axios error fallback
  return {
    isAuthIssue: false,
    error:
      (error as TryCatchError)?.message ||
      "An unexpected error occurred. Please try again.",
  };
};
